import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { DashboardsContext } from '../../../../../contexts/DashboardsContext';
import { updateDashboardFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { Dashboard } from '../../../../../types/files';

const localStyles = makeStyles((theme) => ({
    syncExternally: {
        width: '18ch',
        padding: 'unset',
        height: 40,
        borderRadius: 32,
        color: theme.palette.primary.main,
        background: '#fff',
        borderWidth: 2,
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
    },
    synced: {
        width: '18ch',
        padding: 'unset',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
        "&.Mui-disabled": {
            color: '#8aba71',
            background: 'transparent',
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: 'bold',
            textTransform: 'none',
        },
    },
}));

const SyncStatusCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const localClasses = localStyles();
    const { setDashboards } = useContext(DashboardsContext);
    const [loading, setLoading] = useState<string>('');
    const [syncExternally, setSyncExternally] = useState<boolean>(false);

    const handleSyncExternal = useCallback(async () => {
        const updatedDashboard = { ...dashboard, shouldSyncExternally: true };

        setLoading('sync');
        await updateDashboardFunc(updatedDashboard);
        setDashboards(prev => prev.map(d => d.id === updatedDashboard.id ? updatedDashboard : d));
        setSyncExternally(true);
        setLoading('');
    }, [dashboard, setDashboards]);

    useEffect(() => {
        setSyncExternally(!!dashboard?.shouldSyncExternally);
    }, [dashboard, handleSyncExternal]);

    return (<>
        {!syncExternally ? (
            <Button variant="outlined"
                className={localClasses.syncExternally}
                startIcon={(loading === 'sync') ? (
                    <CircularProgress size={24} sx={{ color: theme.palette.primary.light }} />
                ): (<CachedIcon sx={{ fontSize: "28px !important", color: theme.colors.cyan['500'] }} />)}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSyncExternal();
                }} disableRipple>{'Sync externally'} </Button>
        ) : (
            <Button variant="text"
                className={localClasses.synced}
                startIcon={<CheckCircleIcon sx={{ fontSize: "28px !important", color: '#8aba71' }} />}
                disabled> {'Sync complete'} </Button>
        )}
    </>);
}

export default memo(SyncStatusCell);
